/*
<summary>
   This functional component is all about rendering the Tool Management inline filter bar
   (Owner Factory, Tool Management No, Tool Name, Status, Calibration Status),
   styled like the BOM Search module's inline filter row.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Formik, Form as FormikForm } from 'formik';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { ToolManagementFilterInitialState } from '../../../core/initialState/filter-initial-state';
import { IOption } from '../../../models/ICommon';
import { IToolManagementFilter } from '../../../models/IToolManagementFilter';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormSelect from '../../../shared-components/FormikFormHandlers/FormikFormSelect';
import { ToolFilterValidationSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import { PlusIcon } from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';
import { ToolCalibrationStatusOptions, ToolInspectionStatusOptions, ToolUsageStatusOptions } from '../option';

const STATUS_ALL_LABELS = { please_select: formatMessage('all') };

interface IProps {
  initialValues: IToolManagementFilter;
  submitHandler: (data: IToolManagementFilter) => void;
  filterParmas: (data: IToolManagementFilter, isfilterCleard: boolean) => void;
  isLoading: boolean;
  isFilterCleared: boolean;
  factoryOptions: IOption[];
  showAddToolButton?: boolean;
  onAddToolClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
* This functional component renders the Tool Management inline filter bar and hands the
* applied/cleared filter values back to the parent grid.
* @param props: Initial filter values, submit/clear handlers and factory options
*/
const FilterComponent = (props: IProps) => {
  return (
    <React.Fragment>
      <Formik
        initialValues={props.initialValues}
        enableReinitialize
        validateOnBlur={false}
        validationSchema={ToolFilterValidationSchema}
        onSubmit={async (values: IToolManagementFilter) => {
          props.submitHandler(values);
        }}
      >
        {({ resetForm }) => (
          <FormikForm className="user-form pt-2 px-4">
            {props.isLoading && <FormLoader loading={props.isLoading} />}
            <Row className="g-2">
              <Col md={2} lg xl>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="owner_factory"
                    htmlFor="OwnerFactoryId"
                  />
                  <Field
                    data-testid="OwnerFactoryId"
                    name="OwnerFactoryId"
                    as={FormikFormSelect}
                    options={props.factoryOptions}
                    className="form-control"
                  />
              </Col>
              <Col md={2} lg xl>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="tool_management_no"
                    htmlFor="ToolManagementNo"
                  />
                  <Field
                    data-testid="ToolManagementNo"
                    className="form-control"
                    name="ToolManagementNo"
                    autoComplete="off"
                    placeholder="tool_management_no_placeholder"
                    as={FormikFormInput}
                  />
              </Col>
              <Col md={2} lg xl>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="tool_name_equipment_name"
                    htmlFor="ToolName"
                  />
                  <Field
                    data-testid="ToolName"
                    className="form-control"
                    name="ToolName"
                    autoComplete="off"
                    placeholder="tool_name_placeholder"
                    as={FormikFormInput}
                  />
              </Col>
              {/* <Col>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="terminal_name"
                    htmlFor="SerialNo"
                  />
                  <Field
                    data-testid="SerialNo"
                    className="form-control"
                    name="SerialNo"
                    autoComplete="off"
                    placeholder="terminal_name_placeholder"
                    as={FormikFormInput}
                  />
              </Col> */}
              <Col md={2} lg xl>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="inspection_status"
                    htmlFor="InspectionStatus"
                  />
                  <Field
                    data-testid="InspectionStatus"
                    name="InspectionStatus"
                    as={FormikFormSelect}
                    options={ToolInspectionStatusOptions}
                    labels={STATUS_ALL_LABELS}
                    className="form-control"
                  />
              </Col>
              <Col md={2} lg xl>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="usage_status"
                    htmlFor="UsageStatus"
                  />
                  <Field
                    data-testid="UsageStatus"
                    name="UsageStatus"
                    as={FormikFormSelect}
                    options={ToolUsageStatusOptions}
                    labels={STATUS_ALL_LABELS}
                    className="form-control"
                  />
              </Col>
              <Col md={2} lg xl>
                  <Label
                    className="label-style col-form-label text-truncate d-block"
                    required={false}
                    label="calibration_status"
                    htmlFor="CalibrationStatus"
                  />
                  <Field
                    data-testid="CalibrationStatus"
                    name="CalibrationStatus"
                    as={FormikFormSelect}
                    options={ToolCalibrationStatusOptions}
                    labels={STATUS_ALL_LABELS}
                    className="form-control"
                  />
              </Col>
              <Col
                xs="auto"
                className="d-flex gap-2 justify-content-end clear-filter-btn bom-serch-btn align-items-end"
              >
                  <Button
                    variant="secondary"
                    type="submit"
                  >
                    <FormattedMessage id="search" />
                  </Button>
                  <Button
                    className="text-white bom-clear-btn"
                    disabled={props.isFilterCleared}
                    variant="danger"
                    onClick={() => {
                      props.filterParmas(ToolManagementFilterInitialState, true);
                      resetForm();
                    }}
                  >
                    <FormattedMessage id="clear" />
                  </Button>
                  {props.showAddToolButton && (
                    <Button
                      className="filter-add-tool-btn"
                      variant="secondary"
                      type="button"
                      title={formatMessage('add_tool_management')}
                      onClick={props.onAddToolClick}
                    >
                      <span className="filter-add-tool-btn-icon me-1">
                        <PlusIcon />
                      </span>
                      <span className="filter-add-tool-btn-text">
                        <FormattedMessage id="add_tool_management" />
                      </span>
                    </Button>
                  )}
              </Col>
            </Row>
          </FormikForm>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default FilterComponent;
